import { supabase } from '../../integrations/supabase/client';
import { OFXParser } from '../parsers/OFXParser';
import { BankMappingService } from '../bank-mappings/BankMappingService';
import { 
  BankStatement, 
  BankTransaction, 
  OFXFile, 
  OFXStatement, 
  BankCode,
  ProcessingResult
} from '../../types/bank-reconciliation';

export interface ProcessStatementOptions {
  file: File;
  bankCode: BankCode;
  paymentAccountId: string;
  userId: string;
}

export interface ProcessingStatus {
  status: 'processing' | 'completed' | 'error';
  progress: number;
  message: string;
  totalTransactions?: number;
  processedTransactions?: number;
  errors?: string[];
  warnings?: string[];
}

export class BankStatementProcessor {
  private static readonly BATCH_SIZE = 100;

  static async processStatement(options: ProcessStatementOptions): Promise<ProcessingResult> {
    const { file, bankCode, paymentAccountId, userId } = options;

    try {
      // Step 1: Parse OFX file
      const ofxData = await OFXParser.parseFile(file);
      
      if (!ofxData.statements.length) {
        throw new Error('Nenhum extrato encontrado no arquivo');
      }

      const ofxStatement = ofxData.statements[0];

      // Step 2: Create bank statement record
      const bankStatement = await this.createBankStatementRecord({
        userId,
        paymentAccountId,
        bankCode,
        file,
        ofxStatement
      });

      // Step 3: Process and insert transactions
      const transactions = await this.processTransactions(
        ofxStatement,
        bankStatement.id,
        bankCode
      );

      // Step 4: Update statement with final counts
      await this.updateStatementCounts(bankStatement.id, transactions.length);

      return {
        success: true,
        statement: bankStatement,
        transactions
      };

    } catch (error) {
      console.error('Error processing bank statement:', error);
      return {
        success: false,
        errors: [error instanceof Error ? error.message : 'Erro desconhecido']
      };
    }
  }

  private static async createBankStatementRecord(params: {
    userId: string;
    paymentAccountId: string;
    bankCode: BankCode;
    file: File;
    ofxStatement: OFXStatement;
  }): Promise<BankStatement> {
    const { userId, paymentAccountId, bankCode, file, ofxStatement } = params;

    // Skip file upload to Supabase Storage (avoiding storage errors)
    // We'll process the file content directly without storing it
    const filePath = undefined;

    const bankName = this.getBankName(bankCode);

    const statementData = {
      user_id: userId,
      payment_account_id: paymentAccountId,
      bank_code: bankCode,
      bank_name: bankName,
      file_name: file.name,
      file_path: filePath,
      file_size: file.size,
      statement_date_from: ofxStatement.dateStart || new Date(),
      statement_date_to: ofxStatement.dateEnd || new Date(),
      opening_balance: ofxStatement.availableBalance?.amount || 0,
      closing_balance: ofxStatement.balance.amount,
      total_transactions: ofxStatement.transactions.length,
      status: 'processing' as const
    };

    const { data, error } = await supabase
      .from('bank_statements')
      .insert(statementData)
      .select()
      .single();

    if (error) {
      throw new Error(`Erro ao criar registro do extrato: ${error.message}`);
    }

    return data;
  }

  private static async processTransactions(
    ofxStatement: OFXStatement,
    bankStatementId: string,
    bankCode: BankCode
  ): Promise<BankTransaction[]> {
    const transactions: BankTransaction[] = [];
    const batchSize = this.BATCH_SIZE;

    for (let i = 0; i < ofxStatement.transactions.length; i += batchSize) {
      const batch = ofxStatement.transactions.slice(i, i + batchSize);
      const processedBatch = await this.processBatch(batch, bankStatementId, bankCode);
      transactions.push(...processedBatch);
    }

    return transactions;
  }

  private static async processBatch(
    ofxTransactions: any[],
    bankStatementId: string,
    bankCode: BankCode
  ): Promise<BankTransaction[]> {
    const transactionData = ofxTransactions.map(ofxTrn => {
      // Process transaction with bank-specific mapping
      const processedData = BankMappingService.processTransaction(ofxTrn, bankCode);

      return {
        bank_statement_id: bankStatementId,
        transaction_date: ofxTrn.datePosted,
        value_date: ofxTrn.dateUser || ofxTrn.dateAvailable,
        description: processedData.description || 'Transação sem descrição',
        original_description: processedData.original_description || '',
        amount: ofxTrn.amount,
        transaction_type: processedData.transaction_type || (ofxTrn.amount >= 0 ? 'credit' : 'debit'),
        reference_number: processedData.reference_number || ofxTrn.refNum,
        document_number: processedData.document_number || ofxTrn.checkNum,
        bank_category: processedData.bank_category,
        fit_id: ofxTrn.fitId,
        is_matched: false
      };
    });

    const { data, error } = await supabase
      .from('bank_transactions')
      .insert(transactionData)
      .select();

    if (error) {
      throw new Error(`Erro ao inserir transações: ${error.message}`);
    }

    return data;
  }

  private static async updateStatementCounts(statementId: string, totalTransactions: number): Promise<void> {
    const { error } = await supabase
      .from('bank_statements')
      .update({
        total_transactions: totalTransactions,
        status: 'completed',
        updated_at: new Date().toISOString()
      })
      .eq('id', statementId);

    if (error) {
      console.error('Error updating statement counts:', error);
    }
  }

  // Bank statement queries
  static async getBankStatements(userId: string, limit = 20): Promise<BankStatement[]> {
    const { data, error } = await supabase
      .from('bank_statements')
      .select(`
        *,
        payment_accounts!inner (
          name,
          bank_name
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Erro ao buscar extratos: ${error.message}`);
    }

    return data || [];
  }

  static async getBankStatement(statementId: string, userId: string): Promise<BankStatement | null> {
    const { data, error } = await supabase
      .from('bank_statements')
      .select(`
        *,
        payment_accounts!inner (
          name,
          bank_name
        )
      `)
      .eq('id', statementId)
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Erro ao buscar extrato: ${error.message}`);
    }

    return data;
  }

  static async getBankTransactions(
    statementId: string, 
    userId: string,
    options: {
      limit?: number;
      offset?: number;
      matchedOnly?: boolean;
      unmatchedOnly?: boolean;
    } = {}
  ): Promise<BankTransaction[]> {
    const { limit = 50, offset = 0, matchedOnly, unmatchedOnly } = options;

    let query = supabase
      .from('bank_transactions')
      .select(`
        *,
        bank_statements!inner (
          user_id
        )
      `)
      .eq('bank_statement_id', statementId)
      .eq('bank_statements.user_id', userId)
      .order('transaction_date', { ascending: false })
      .range(offset, offset + limit - 1);

    if (matchedOnly) {
      query = query.eq('is_matched', true);
    } else if (unmatchedOnly) {
      query = query.eq('is_matched', false);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Erro ao buscar transações: ${error.message}`);
    }

    return data || [];
  }

  // Matching functionality
  static async autoMatch(statementId: string, userId: string): Promise<{
    matched: number;
    suggested: number;
    errors: string[];
  }> {
    try {
      // Get unmatched bank transactions
      const bankTransactions = await this.getBankTransactions(statementId, userId, {
        unmatchedOnly: true,
        limit: 1000
      });

      // Get system transactions in the same period
      const statement = await this.getBankStatement(statementId, userId);
      if (!statement) {
        throw new Error('Extrato não encontrado');
      }

      const systemTransactions = await this.getSystemTransactions(
        userId,
        statement.statement_date_from,
        statement.statement_date_to
      );

      let matched = 0;
      let suggested = 0;
      const errors: string[] = [];

      // Simple matching algorithm
      for (const bankTrn of bankTransactions) {
        const matches = this.findMatches(bankTrn, systemTransactions);
        
        if (matches.length === 1 && matches[0].confidence >= 0.9) {
          // Auto-match with high confidence
          try {
            await this.createMatch(bankTrn.id, matches[0].transaction.id, 'automatic', matches[0].confidence, userId);
            matched++;
          } catch (error) {
            errors.push(`Erro ao criar match para transação ${bankTrn.id}`);
          }
        } else if (matches.length > 0) {
          // Create suggestions for manual review
          suggested++;
        }
      }

      return { matched, suggested, errors };

    } catch (error) {
      return {
        matched: 0,
        suggested: 0,
        errors: [error instanceof Error ? error.message : 'Erro desconhecido']
      };
    }
  }

  private static async getSystemTransactions(userId: string, dateFrom: Date, dateTo: Date): Promise<any[]> {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .gte('date', dateFrom.toISOString().split('T')[0])
      .lte('date', dateTo.toISOString().split('T')[0])
      .is('matched_bank_transaction_id', null); // Only unmatched transactions

    if (error) {
      throw new Error(`Erro ao buscar transações do sistema: ${error.message}`);
    }

    return data || [];
  }

  private static findMatches(bankTransaction: BankTransaction, systemTransactions: any[]): Array<{
    transaction: any;
    confidence: number;
    reasons: string[];
  }> {
    const matches: Array<{ transaction: any; confidence: number; reasons: string[] }> = [];

    for (const sysTrn of systemTransactions) {
      const match = this.calculateMatch(bankTransaction, sysTrn);
      if (match.confidence > 0.5) {
        matches.push(match);
      }
    }

    return matches.sort((a, b) => b.confidence - a.confidence);
  }

  private static calculateMatch(bankTransaction: BankTransaction, systemTransaction: any): {
    transaction: any;
    confidence: number;
    reasons: string[];
  } {
    let confidence = 0;
    const reasons: string[] = [];

    // Amount match (most important)
    const amountDiff = Math.abs(bankTransaction.amount - Math.abs(systemTransaction.amount));
    if (amountDiff === 0) {
      confidence += 0.5;
      reasons.push('Valor exato');
    } else if (amountDiff <= 0.01) {
      confidence += 0.4;
      reasons.push('Valor aproximado');
    }

    // Date proximity
    const bankDate = new Date(bankTransaction.transaction_date);
    const sysDate = new Date(systemTransaction.date);
    const daysDiff = Math.abs((bankDate.getTime() - sysDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff === 0) {
      confidence += 0.3;
      reasons.push('Data exata');
    } else if (daysDiff <= 1) {
      confidence += 0.2;
      reasons.push('Data próxima');
    } else if (daysDiff <= 3) {
      confidence += 0.1;
      reasons.push('Data compatível');
    }

    // Description similarity
    if (systemTransaction.description) {
      const similarity = this.calculateTextSimilarity(
        bankTransaction.description.toLowerCase(),
        systemTransaction.description.toLowerCase()
      );
      
      if (similarity > 0.8) {
        confidence += 0.2;
        reasons.push('Descrição similar');
      } else if (similarity > 0.5) {
        confidence += 0.1;
        reasons.push('Descrição compatível');
      }
    }

    return {
      transaction: systemTransaction,
      confidence: Math.min(1, confidence),
      reasons
    };
  }

  private static calculateTextSimilarity(text1: string, text2: string): number {
    // Simple Levenshtein distance-based similarity
    const longer = text1.length > text2.length ? text1 : text2;
    const shorter = text1.length > text2.length ? text2 : text1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  private static async createMatch(
    bankTransactionId: string,
    systemTransactionId: string,
    matchType: 'automatic' | 'manual',
    confidence: number,
    userId: string
  ): Promise<void> {
    // Update bank transaction
    const { error: bankError } = await supabase
      .from('bank_transactions')
      .update({
        is_matched: true,
        matched_transaction_id: systemTransactionId,
        match_confidence: confidence,
        match_type: matchType,
        updated_at: new Date().toISOString()
      })
      .eq('id', bankTransactionId);

    if (bankError) {
      throw new Error(`Erro ao atualizar transação bancária: ${bankError.message}`);
    }

    // Create reconciliation log
    const { error: logError } = await supabase
      .from('reconciliation_log')
      .insert({
        user_id: userId,
        bank_transaction_id: bankTransactionId,
        system_transaction_id: systemTransactionId,
        match_type: matchType,
        match_confidence: confidence,
        reconciled_by: userId
      });

    if (logError) {
      console.error('Error creating reconciliation log:', logError);
    }
  }

  private static getBankName(bankCode: BankCode): string {
    const bankNames: Record<BankCode, string> = {
      '001': 'Banco do Brasil',
      '033': 'Santander',
      '104': 'Caixa Econômica Federal',
      '237': 'Bradesco',
      '260': 'Nubank',
      '341': 'Itaú',
      '077': 'Inter',
      '208': 'BTG Pactual',
      '756': 'Sicoob',
      '748': 'Sicredi'
    };

    return bankNames[bankCode] || bankCode;
  }
}